import { default as OLMap } from 'ol/Map'
import View from 'ol/View'
import {
  DragPan,
  MouseWheelZoom,
  DoubleClickZoom,
  PinchZoom,
} from 'ol/interaction'
import { fromLonLat } from 'ol/proj'
import { Attribution, Zoom } from 'ol/control'
import SplitMapContext from 'context/split-map-context'
import LazyRender from 'components/lazy-render'
import { isNil, get } from 'es-toolkit/compat'
import * as c from 'common/c'

import { GlobalStyles } from '@mui/material'

const olGlobalStyles = (
  <GlobalStyles
    styles={{
      '.ol-zoom': {
        bottom: '3em !important',
        top: 'auto !important',
      },
      '.ol-zoom .ol-zoom-out': {
        marginTop: '200px',
        '@media screen and (max-height: 1000px)': { marginTop: 0 },
      },
      '.ol-touch .ol-zoom .ol-zoom-out': {
        marginTop: '212px',
        '@media screen and (max-height: 1000px)': { marginTop: 0 },
      },
    }}
  />
)

const SplitMapProvider = (props) => {
  const { children } = props

  const [map_a, setMap_a] = useState()
  const [map_b, setMap_b] = useState()
  const [lock_a, setLockA] = useState(true)
  const [lock_b, setLockB] = useState(true)
  const fieldA = useSelector((state) => get(state, 'field.field_a'))
  const fieldB = useSelector((state) => get(state, 'field.field_b'))

  const onSwitchLockA = useCallback(() => {
    setLockA(!lock_a)
  }, [lock_a])

  const onSwitchLockB = useCallback(() => {
    setLockB(!lock_b)
  }, [lock_b])

  useEffect(() => {
    if (fieldA?.id !== fieldB?.id) {
      setLockA(false)
      setLockB(false)
    }
  }, [fieldA?.id, fieldB?.id])

  useEffect(() => {
    const viewA = new View({
      zoom: c.DEFAULT_ZOOM,
      center: fromLonLat(c.DEFAULT_CENTER),
      enableRotation: false,
      projection: 'EPSG:3857',
      maxZoom: 21,
    })

    // default controls. The Attribution control carries the Esri credit the
    // base map requires — see common/ol/arcgis-layers.
    const controlsA = [new Zoom(), new Attribution({ collapsible: true })]

    // default interactions
    const interactionsA = [
      new DragPan(),
      new MouseWheelZoom(),
      new DoubleClickZoom(),
      new PinchZoom(),
    ]

    // default layers
    const layersA = []

    const mapPropsA = {
      view: viewA,
      layers: layersA,
      controls: controlsA,
      overlays: [],
      interactions: interactionsA,
    }

    const mapInstanceA = new OLMap(mapPropsA)

    setMap_a(mapInstanceA)

    const viewB = new View({
      zoom: c.DEFAULT_ZOOM,
      center: fromLonLat(c.DEFAULT_CENTER),
      enableRotation: false,
      projection: 'EPSG:3857',
      maxZoom: 21,
    })

    // default controls
    const controlsB = [new Zoom(), new Attribution({ collapsible: true })]

    // default interactions
    const interactionsB = [
      new DragPan(),
      new MouseWheelZoom(),
      new DoubleClickZoom(),
      new PinchZoom(),
    ]

    // default layers
    const layersB = []

    const mapPropsB = {
      view: viewB,
      layers: layersB,
      controls: controlsB,
      overlays: [],
      interactions: interactionsB,
    }

    const mapInstanceB = new OLMap(mapPropsB)

    setMap_b(mapInstanceB)

    return function cleanup() {
      mapInstanceA.setTarget(undefined)
      mapInstanceB.setTarget(undefined)
    }
  }, [])

  const isSyncingViewsRef = useRef(false)

  const onViewChangeA = useCallback(
    (e) => {
      if (isSyncingViewsRef.current) {
        return
      }
      if (lock_a && fieldA?.id === fieldB?.id) {
        let center = map_a.getView().getCenter()
        let zoom = map_a.getView().getZoom()
        isSyncingViewsRef.current = true
        map_b.getView().setCenter(center)
        map_b.getView().setZoom(zoom)
        isSyncingViewsRef.current = false
      }
    },
    [lock_a, map_a, map_b, fieldA, fieldB],
  )

  const onViewChangeB = useCallback(
    (e) => {
      if (isSyncingViewsRef.current) {
        return
      }
      if (lock_a && fieldA?.id === fieldB?.id) {
        let center = map_b.getView().getCenter()
        let zoom = map_b.getView().getZoom()
        isSyncingViewsRef.current = true
        map_a.getView().setCenter(center)
        map_a.getView().setZoom(zoom)
        isSyncingViewsRef.current = false
      }
    },
    [lock_a, map_a, map_b, fieldA, fieldB],
  )

  useEffect(() => {
    if (map_a && map_b && fieldA?.id === fieldB?.id) {
      map_a.getView().on(['change:center', 'change:resolution'], onViewChangeA)
      map_b.getView().on(['change:center', 'change:resolution'], onViewChangeB)
    }

    return function () {
      if (map_a && map_b) {
        map_a
          .getView()
          .un(['change:center', 'change:resolution'], onViewChangeA)
        map_b
          .getView()
          .un(['change:center', 'change:resolution'], onViewChangeB)
      }
    }
  }, [map_a, map_b, lock_a, lock_b, fieldA, fieldB, onViewChangeA, onViewChangeB])

  const isDone = useMemo(() => {
    return !isNil(map_a) && !isNil(map_b)
  }, [map_a, map_b])

  return (
    <>
      {olGlobalStyles}
      <LazyRender
        done={isDone}
        render={() => {
          return (
            <SplitMapContext.Provider
              value={{
                map_a,
                map_b,
                lock_a,
                onSwitchLockA,
                lock_b,
                onSwitchLockB,
              }}
            >
              {children}
            </SplitMapContext.Provider>
          )
        }}
      />
    </>
  )
}

export default SplitMapProvider
